/**
 * Learning Outcome (LO) linting.
 * Provides quality checks for learning outcome text including vague verb detection.
 * @module lib/lo-lint
 */

import { DEFAULT_GUIDANCE } from "@/taxonomy/registry";
import type { GuidanceStrategy, VerbMatch } from "@/taxonomy/strategy";
import type { TaxonomyLevel } from "@/taxonomy/types";
import { VAGUE_VERBS } from "@/taxonomy/vague-verbs";

import { detectLanguage, type LanguageResult, normaliseText } from "./language-detect";

/** A single lint issue found in a learning outcome. */
export interface LintIssue {
  id: string;
  severity: "warn" | "info";
  match: string;
  message: string;
  suggestions: string[];
}

/** Result of linting a single learning outcome. */
export interface LintResult {
  issues: LintIssue[];
  language: LanguageResult;
}

/** Options for linting learning outcomes. */
export interface LintOptions {
  expectedLanguage?: string;
  allowUnknownLanguage?: boolean;
  language?: { minTokens?: number };
  /** The programme's level in the strategy's framework. Enables level checks. */
  frameworkLevel?: number | null;
  /** Taxonomy and framework to check against; Bloom's and the NFQ by default. */
  strategy?: GuidanceStrategy;
}

type Ranked = TaxonomyLevel & { rank: number };
const isRanked = (l: TaxonomyLevel): l is Ranked => l.rank !== null;

/**
 * The nearest candidate outside the allowed levels, or null when any candidate is
 * inside or rank cannot say which side it falls on.
 */
function placeVerb(
  levels: readonly TaxonomyLevel[],
  allowed: readonly TaxonomyLevel[],
): { direction: "high" | "low"; level: Ranked } | null {
  if (levels.some((l) => allowed.includes(l))) {
    return null;
  }
  const ranks = allowed.filter(isRanked).map((l) => l.rank);
  const candidates = levels.filter(isRanked);
  if (ranks.length === 0) {
    return null;
  }
  const above = candidates.filter((l) => l.rank > Math.max(...ranks));
  if (above.length > 0) {
    return { direction: "high", level: above.reduce((a, b) => (b.rank < a.rank ? b : a)) };
  }
  const below = candidates.filter((l) => l.rank < Math.min(...ranks));
  if (below.length > 0) {
    return { direction: "low", level: below.reduce((a, b) => (b.rank > a.rank ? b : a)) };
  }
  return null;
}

/**
 * Check verbs against the levels the framework level emphasises.
 * A verb passes when any level it can express is primary or secondary there;
 * otherwise it is reported above (`bloom_level_high`) or below (`bloom_level_low`) the range.
 */
function checkLevel(
  verbs: VerbMatch[],
  strategy: GuidanceStrategy,
  frameworkLevel: number,
): LintIssue[] {
  const emphasis = strategy.emphasisFor(frameworkLevel);
  if (!emphasis) {
    return [];
  }
  const { taxonomy, framework } = strategy;
  const byId = (id: string) => taxonomy.levels.filter((l) => l.id === id);
  const primary = emphasis.primary.flatMap(byId);
  const allowed = [...primary, ...emphasis.secondary.flatMap(byId)];
  const primaryNames = primary.map((l) => l.name).join(" / ");
  const suggestions = primary.flatMap((l) => l.verbs.slice(0, 3).map((v) => v.lemma));

  return verbs.flatMap(({ verb, levels }) => {
    const placed = placeVerb(levels, allowed);
    if (!placed) {
      return [];
    }
    const { rank, name } = placed.level;
    const relation = placed.direction === "high" ? "above" : "below";
    return [
      {
        id: `bloom_level_${placed.direction}`,
        severity: "info" as const,
        match: verb,
        message: `"${verb}" is ${taxonomy.shortName} level ${rank} (${name}), ${relation} the range for ${framework.shortName} ${frameworkLevel}. Consider verbs from ${primaryNames}.`,
        suggestions,
      },
    ];
  });
}

/**
 * How many distinct cognitive actions the verbs name: verbs whose level sets
 * overlap, directly or through another verb, count as one.
 */
export function countCognitiveActions(verbs: VerbMatch[]): number {
  const groups: Set<string>[] = [];
  verbs.forEach(({ levels }) => {
    const merged = new Set(levels.map((l) => l.id));
    for (let i = groups.length - 1; i >= 0; i -= 1) {
      if ([...groups[i]].some((id) => merged.has(id))) {
        groups[i].forEach((id) => merged.add(id));
        groups.splice(i, 1);
      }
    }
    groups.push(merged);
  });
  return groups.length;
}

/**
 * Detect multiple action verbs in an LO, suggesting the outcome might be split.
 * Fires only for 2+ distinct cognitive actions, so "compare and contrast" is one.
 */
function checkMultipleVerbs(verbs: VerbMatch[]): LintIssue | null {
  if (countCognitiveActions(verbs) < 2) {
    return null;
  }
  const verbList = verbs.map((v) => `"${v.verb}"`).join(", ");
  return {
    id: "multiple_verbs",
    severity: "info",
    match: verbs.map((v) => v.verb).join(", "),
    message: `This outcome has several action verbs (${verbList}). You could split it into separate outcomes.`,
    suggestions: [],
  };
}

export function lintLearningOutcome(text: string, opts: LintOptions = {}): LintResult {
  const t = normaliseText(text);
  const issues: LintIssue[] = [];

  if (!t) {
    return { issues, language: { lang: "unknown", confidence: 0, scores: {} } };
  }

  const language = detectLanguage(t, opts.language);

  const expected = (opts.expectedLanguage ?? "en").toLowerCase();
  const allowUnknown = opts.allowUnknownLanguage ?? true;

  if (language.lang !== "unknown") {
    if (language.lang !== expected) {
      issues.push({
        id: "language_mismatch",
        severity: "warn",
        match: "",
        message: `Detected language looks like "${language.lang}" (expected "${expected}").`,
        suggestions: [],
      });
    }
  } else if (!allowUnknown) {
    issues.push({
      id: "language_unknown",
      severity: "info",
      match: "",
      message: "Could not detect language reliably (short text).",
      suggestions: [],
    });
  }

  // matchAll iterates a clone of the regex, so the shared pattern keeps its own
  // lastIndex and one call's starting position never depends on the last.
  VAGUE_VERBS.forEach((rule) => {
    [...t.matchAll(rule.pattern)].forEach((m) => {
      issues.push({
        id: rule.id,
        severity: "warn",
        match: m[0],
        message: rule.message,
        suggestions: [...rule.suggestions],
      });
    });
  });

  const strategy = opts.strategy ?? DEFAULT_GUIDANCE;
  const verbs = strategy.classify(t);

  if (opts.frameworkLevel != null) {
    issues.push(...checkLevel(verbs, strategy, Number(opts.frameworkLevel)));
  }

  const multiVerbIssue = checkMultipleVerbs(verbs);
  if (multiVerbIssue) {
    issues.push(multiVerbIssue);
  }

  return { issues, language };
}

/**
 * Lints multiple learning outcomes.
 */
export function lintLearningOutcomes(
  outcomes: string[],
  opts: LintOptions = {},
): ({ index: number; text: string } & LintResult)[] {
  return (outcomes ?? []).map((loText, idx) => ({
    index: idx,
    text: loText,
    ...lintLearningOutcome(loText, opts),
  }));
}
